import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  X,
  MapPin,
  Search,
  Droplets,
  CloudRain,
  Thermometer,
  Clock,
  Compass,
  Check,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import WeatherIcon from "./WeatherIcon";
import {
  getWeatherMeta,
  searchCities,
  saveLocation,
  DEFAULT_LOCATION,
} from "../services/weatherService";
import { formatDateSafe } from "../utils/dateUtils";

export default function WeatherDetailModal({
  open,
  onClose,
  dayWeather,
  currentLocation,
  onLocationChange,
}) {
  const [activeTab, setActiveTab] = useState("work"); // 'work' (09h-Minuit) | 'all' (24h)
  const [showLocationSearch, setShowLocationSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [locError, setLocError] = useState(null);
  const searchInputRef = useRef(null);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Focus search input when city selector opens
  useEffect(() => {
    if (showLocationSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showLocationSearch]);

  // Debounced search for cities
  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await searchCities(searchQuery);
        setSearchResults(results);
      } catch (err) {
        console.error(err);
      } finally {
        setSearchLoading(false);
      }
    }, 280);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelectCity = (city) => {
    saveLocation(city);
    if (onLocationChange) onLocationChange(city);
    setShowLocationSearch(false);
    setSearchQuery("");
  };

  const handleUseCurrentPosition = () => {
    if (!navigator.geolocation) {
      setLocError("La géolocalisation n'est pas supportée par votre navigateur.");
      return;
    }

    setSearchLoading(true);
    setLocError(null);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newLoc = {
          name: "Ma position",
          admin1: "GPS",
          country: "",
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        };
        saveLocation(newLoc);
        if (onLocationChange) onLocationChange(newLoc);
        setShowLocationSearch(false);
        setSearchLoading(false);
      },
      (err) => {
        console.warn("Erreur géolocalisation", err);
        setLocError("Autorisation refusée ou position indisponible.");
        setSearchLoading(false);
      },
      { timeout: 8000 }
    );
  };

  const handleResetToDefault = () => {
    saveLocation(DEFAULT_LOCATION);
    if (onLocationChange) onLocationChange(DEFAULT_LOCATION);
    setShowLocationSearch(false);
  };

  // Filter hourly data based on selected tab
  const filteredHourly = useMemo(() => {
    if (!dayWeather || !dayWeather.hourly) return [];
    if (activeTab === "all") return dayWeather.hourly;

    // 'work': De 09:00 à 23:00 inclus (jusqu'à minuit)
    return dayWeather.hourly.filter((h) => {
      const hourNum = parseInt(h.hour.split(":")[0], 10);
      return hourNum >= 9 && hourNum <= 23;
    });
  }, [dayWeather, activeTab]);

  if (!open || !dayWeather) return null;

  const meta = getWeatherMeta(dayWeather.weatherCode);

  // Formater la date en français clair (ex: "Lundi 12 Octobre 2026")
  const formattedDateTitle = formatDateSafe(dayWeather.date, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const capitalizedDateTitle =
    formattedDateTitle.charAt(0).toUpperCase() + formattedDateTitle.slice(1);

  // Gradient per weather condition
  const getHeroGradient = (code) => {
    if (code === 0 || code === 1) {
      return "from-amber-500 via-orange-500 to-amber-600 text-white";
    }
    if (code === 2) {
      return "from-sky-500 via-indigo-500 to-blue-600 text-white";
    }
    if (code >= 51 && code <= 67) {
      return "from-blue-600 via-indigo-600 to-slate-700 text-white";
    }
    if (code >= 80 && code <= 82) {
      return "from-indigo-600 via-blue-600 to-cyan-700 text-white";
    }
    if (code >= 95) {
      return "from-purple-700 via-indigo-800 to-slate-900 text-white";
    }
    if (code >= 71 && code <= 77) {
      return "from-cyan-600 via-sky-600 to-slate-700 text-white";
    }
    return "from-slate-600 via-gray-700 to-zinc-800 text-white";
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-3 sm:p-4 animate-[fadeIn_0.15s_ease-out]"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden animate-[popIn_0.18s_ease-out] border border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header bar */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-white relative z-20">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
              <Compass className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">
                Météo détaillée
              </h2>
              <p className="text-xs text-gray-500">{capitalizedDateTitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Ville actuelle & changement */}
            <button
              onClick={() => setShowLocationSearch(!showLocationSearch)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700 text-xs font-medium transition-colors"
              title="Changer de ville"
            >
              <MapPin className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
              <span className="max-w-[140px] truncate">
                {currentLocation?.name || "Lagny-sur-Marne"}
              </span>
              <span className="text-[10px] text-indigo-600 font-semibold underline ml-0.5">
                Changer
              </span>
            </button>

            {/* Fermer */}
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-700 rounded-xl hover:bg-gray-100 transition-colors"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Location search dropdown modal panel */}
        {showLocationSearch && (
          <div className="p-4 bg-indigo-50/60 border-b border-indigo-100 animate-[fadeIn_0.15s_ease-out]">
            <div className="flex items-center gap-2 mb-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher une ville (ex: Meaux, Torcy, Lyon...)"
                  className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                />
              </div>
              <button
                onClick={handleUseCurrentPosition}
                disabled={searchLoading}
                className="px-3 py-2 bg-white hover:bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-colors disabled:opacity-50"
                title="Détecter la position via GPS"
              >
                <Compass className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">GPS</span>
              </button>
              <button
                onClick={handleResetToDefault}
                className="p-2 bg-white hover:bg-gray-50 border border-gray-200 text-gray-600 rounded-xl text-xs transition-colors shadow-sm"
                title="Rétablir Lagny-sur-Marne par défaut"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            </div>

            {locError && (
              <p className="text-xs text-red-600 mt-1 mb-2 font-medium">⚠️ {locError}</p>
            )}

            {searchLoading && (
              <p className="text-xs text-gray-500 py-2 text-center">Recherche en cours...</p>
            )}

            {searchResults.length > 0 && (
              <div className="mt-2 bg-white rounded-xl border border-gray-200 shadow-md divide-y divide-gray-100 max-h-48 overflow-y-auto">
                {searchResults.map((city, idx) => (
                  <button
                    key={`${city.name}_${city.latitude}_${idx}`}
                    onClick={() => handleSelectCity(city)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-indigo-50 flex items-center justify-between transition-colors"
                  >
                    <div>
                      <span className="font-semibold text-gray-800">{city.name}</span>
                      {city.admin1 && (
                        <span className="text-gray-400 ml-1.5">({city.admin1})</span>
                      )}
                      {city.country && (
                        <span className="text-gray-500 ml-1">· {city.country}</span>
                      )}
                    </div>
                    <Check className="w-3.5 h-3.5 text-indigo-600 opacity-0 group-hover:opacity-100" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Modal Scrollable Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-5 flex-1">
          {/* Hero Banner */}
          <div
            className={`rounded-2xl p-5 sm:p-6 bg-gradient-to-br ${getHeroGradient(
              dayWeather.weatherCode
            )} shadow-lg relative overflow-hidden`}
          >
            <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white/20 backdrop-blur-md">
                  <Sparkles className="w-3 h-3" />
                  {meta.label}
                </span>

                <div className="mt-3 flex items-baseline gap-3">
                  <span className="text-4xl sm:text-5xl font-black tracking-tight">
                    {dayWeather.maxTemp !== null ? `${dayWeather.maxTemp}°` : "--"}
                  </span>
                  <div className="text-xs sm:text-sm text-white/90 font-medium">
                    <div>Max : {dayWeather.maxTemp !== null ? `${dayWeather.maxTemp}°C` : "--"}</div>
                    <div>Min : {dayWeather.minTemp !== null ? `${dayWeather.minTemp}°C` : "--"}</div>
                  </div>
                </div>
              </div>

              {/* Big weather icon */}
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center shadow-inner">
                  <WeatherIcon
                    code={dayWeather.weatherCode}
                    className="w-10 h-10 sm:w-12 sm:h-12"
                    showColor={false}
                  />
                </div>
              </div>
            </div>

            {/* Quick stats badges at bottom of hero */}
            <div className="mt-5 pt-4 border-t border-white/20 grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div className="bg-white/15 backdrop-blur-md rounded-xl p-2.5">
                <div className="flex items-center gap-1 text-[11px] text-white/80">
                  <Droplets className="w-3.5 h-3.5" />
                  <span>Risque de pluie max</span>
                </div>
                <div className="text-base sm:text-lg font-bold mt-0.5">
                  {dayWeather.maxRainProb}%
                </div>
              </div>

              <div className="bg-white/15 backdrop-blur-md rounded-xl p-2.5">
                <div className="flex items-center gap-1 text-[11px] text-white/80">
                  <CloudRain className="w-3.5 h-3.5" />
                  <span>Cumul de pluie prévu</span>
                </div>
                <div className="text-base sm:text-lg font-bold mt-0.5">
                  {dayWeather.rainSum} mm
                </div>
              </div>

              <div className="col-span-2 sm:col-span-1 bg-white/15 backdrop-blur-md rounded-xl p-2.5">
                <div className="flex items-center gap-1 text-[11px] text-white/80">
                  <Thermometer className="w-3.5 h-3.5" />
                  <span>Écart thermique</span>
                </div>
                <div className="text-base sm:text-lg font-bold mt-0.5">
                  {dayWeather.maxTemp !== null && dayWeather.minTemp !== null
                    ? `${dayWeather.maxTemp - dayWeather.minTemp}°C`
                    : "--"}
                </div>
              </div>
            </div>
          </div>

          {/* Section Heure par Heure */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-indigo-600" />
                <h3 className="text-sm sm:text-base font-bold text-gray-900">
                  Prévisions heure par heure & probabilité de pluie
                </h3>
              </div>

              {/* View filter buttons */}
              <div className="flex bg-gray-100 p-0.5 rounded-xl self-start sm:self-auto text-xs font-semibold">
                <button
                  onClick={() => setActiveTab("work")}
                  className={`px-3 py-1 rounded-lg transition-all ${
                    activeTab === "work"
                      ? "bg-white text-indigo-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Horaires travail (09h-Minuit)
                </button>
                <button
                  onClick={() => setActiveTab("all")}
                  className={`px-3 py-1 rounded-lg transition-all ${
                    activeTab === "all"
                      ? "bg-white text-indigo-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  24h
                </button>
              </div>
            </div>

            {/* Hourly Table / Cards */}
            <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/80 border-b border-gray-200 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                      <th className="py-2.5 px-3">Heure</th>
                      <th className="py-2.5 px-3">Météo</th>
                      <th className="py-2.5 px-3 text-center">Temp.</th>
                      <th className="py-2.5 px-3 text-right">Probabilité de pluie</th>
                      <th className="py-2.5 px-3 text-right hidden sm:table-cell">Précipitations</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-xs sm:text-sm">
                    {filteredHourly.map((item) => {
                      const hourMeta = getWeatherMeta(item.weatherCode);

                      // Style rain probability badge
                      let rainBadge = {
                        bg: "bg-gray-100 text-gray-600",
                        barBg: "bg-gray-300",
                        label: "Faible",
                      };
                      if (item.rainProb > 60) {
                        rainBadge = {
                          bg: "bg-blue-600 text-white font-bold",
                          barBg: "bg-blue-600",
                          label: "Très fort",
                        };
                      } else if (item.rainProb > 35) {
                        rainBadge = {
                          bg: "bg-blue-100 text-blue-800 font-semibold",
                          barBg: "bg-blue-500",
                          label: "Modéré",
                        };
                      } else if (item.rainProb > 15) {
                        rainBadge = {
                          bg: "bg-sky-50 text-sky-700",
                          barBg: "bg-sky-400",
                          label: "Léger",
                        };
                      }

                      return (
                        <tr
                          key={item.time}
                          className="hover:bg-indigo-50/40 transition-colors group"
                        >
                          {/* Hour */}
                          <td className="py-2.5 px-3 font-bold text-gray-800 whitespace-nowrap">
                            {item.hour}
                          </td>

                          {/* Icon & Weather condition */}
                          <td className="py-2.5 px-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <WeatherIcon code={item.weatherCode} className="w-4 h-4 flex-shrink-0" />
                              <span className="text-gray-600 text-xs hidden md:inline truncate max-w-[130px]">
                                {hourMeta.label}
                              </span>
                            </div>
                          </td>

                          {/* Temperature */}
                          <td className="py-2.5 px-3 text-center font-bold text-gray-900 whitespace-nowrap">
                            {item.temp !== null ? `${item.temp}°C` : "--"}
                          </td>

                          {/* Precipitation probability with progress bar */}
                          <td className="py-2.5 px-3 text-right whitespace-nowrap">
                            <div className="inline-flex items-center justify-end gap-2">
                              {/* Small bar indicator */}
                              <div className="w-12 h-1.5 bg-gray-100 rounded-full overflow-hidden hidden xs:block">
                                <div
                                  className={`h-full rounded-full transition-all ${rainBadge.barBg}`}
                                  style={{ width: `${Math.min(100, Math.max(4, item.rainProb))}%` }}
                                />
                              </div>

                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-transform group-hover:scale-105 ${rainBadge.bg}`}
                              >
                                {item.rainProb >= 25 && <span>🌧️</span>}
                                <span>{item.rainProb}%</span>
                              </span>
                            </div>
                          </td>

                          {/* Precipitation amount (mm) */}
                          <td className="py-2.5 px-3 text-right text-gray-500 text-xs hidden sm:table-cell whitespace-nowrap">
                            {item.rainMm > 0 ? (
                              <span className="font-semibold text-blue-700">
                                {item.rainMm} mm
                              </span>
                            ) : (
                              <span className="text-gray-300">0 mm</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Footer info & action */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-1">
          </div>

          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 rounded-xl font-semibold transition-colors shadow-sm"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
